<template>
  <span
    class="VisionIcon"
    :class="[
      'VisionIcon--size-' + size,
      'VisionIcon--colour-' + colour,
      gradient ? 'VisionIcon--gradient-' + gradient : '',
    ]"
    :style="styles"
  >
    <svg class="VisionIcon__svg">
      <linearGradient
        v-if="gradient !== 'none'"
        :id="
          'VisionIconGradient--' + gradient + (props.id ? '-' + props.id : '')
        "
        gradientTransform="rotate(20)"
      >
        <stop
          :offset="gradientStop1"
          stop-color="var(--gradient-stop-1-colour)"
        />
        <stop
          v-if="hasGradientStop2"
          :offset="gradientStop2"
          stop-color="var(--gradient-stop-2-colour)"
        />
        <stop
          offset="100%"
          stop-color="var(--gradient-stop-3-colour)"
        />
      </linearGradient>
      <use :href="`#${icon}`" />
    </svg>
  </span>
</template>

<script setup lang="ts">
import type { IconName, IconColour, IconSize } from '../../config/icons.config'
import { computed } from 'vue'

interface IconProps {
  size?: IconSize
  colour?: IconColour
  gradient?: 'none'
    | 'default'
    | 'default-light'
    | 'miami'
    | 'gold'
    | 'dark-chrome'
    | 'ln4'
    | 'custom'
    | 'grape'
    | 'purple'
    | 'orange-dark'
  icon: IconName
  rotate?: number
  id?: string
}

const props = withDefaults(defineProps<IconProps>(), {
  size: 'medium',
  colour: 'white',
  gradient: 'none',
  icon: 'arrow',
  rotate: 0,
  id: '',
})

if (!['none', ''].includes(props.gradient) && !props.id) {
  console.warn(
    'Icons with gradients require a unique :id. The ' +
      props.icon +
      ' icon may not show.',
  )
}

const gradientStop1 = computed(() => {
  switch (props.gradient) {
    case 'gold':
      return '16%'
    case 'custom':
      return '5%'
    default:
      return '0%'
  }
})

const gradientStop2 = computed(() => {
  switch (props.gradient) {
    case 'miami':
      return '40%'
    case 'custom':
      return '45%'
    default:
      return '60%'
  }
})

const hasGradientStop2 = computed(() => {
  return [
    'default',
    'default-light',
    'miami',
    'custom'
  ].includes(props.gradient)
})

const styles = computed(() => {
  if (props.rotate > 0) {
    return {
      transform: 'rotate(' + props.rotate + 'deg)',
    }
  }

  return ''
})

const gradientId = computed(() => {
  let name = '#VisionIconGradient--' + props.gradient

  if (props.id) {
    name += '-' + props.id
  }

  return `url(${name})`
})
</script>

<style lang="postcss" scoped>
.VisionIcon {
  @apply flex items-center transition-transform;
}

.VisionIcon--size-xx-small {
  --size: var(--vision-icon-size, theme('spacing.xxs'));

  .VisionIcon__svg {
    @apply w-[var(--size)] h-[var(--size)];
  }
}

.VisionIcon--size-x-small {
  --size: var(--vision-icon-size, theme('spacing.xs'));

  .VisionIcon__svg {
    @apply w-[var(--size)] h-[var(--size)];
  }
}

.VisionIcon--size-small {
  --size: var(--vision-icon-size, theme('spacing.sm'));

  .VisionIcon__svg {
    @apply w-[var(--size)] h-[var(--size)];
  }
}

.VisionIcon--size-medium {
  --size: var(--vision-icon-size, theme('spacing.7'));

  .VisionIcon__svg {
    @apply w-[var(--size)] h-[var(--size)];
  }
}

.VisionIcon--size-large {
  --size: var(--vision-icon-size, theme('spacing.8'));

  .VisionIcon__svg {
    @apply w-[var(--size)] h-[var(--size)];
  }
}

.VisionIcon--size-x-large {
  --size: var(--vision-icon-size, theme('spacing.10'));

  .VisionIcon__svg {
    @apply w-[var(--size)] h-[var(--size)];
  }
}

.VisionIcon--size-stretch {
  .VisionIcon__svg {
    @apply w-full h-auto aspect-square;
  }
}

.VisionIcon--colour-white {
  .VisionIcon__svg {
    --fill: var(--vision-icon-fill, theme('colors.white'));
    fill: var(--fill);
  }
}

.VisionIcon--colour-black {
  .VisionIcon__svg {
    --fill: var(--vision-icon-fill, theme('colors.off-black'));
    fill: var(--fill);
  }
}

.VisionIcon--colour-grey-dark {
  .VisionIcon__svg {
    --fill: var(--vision-icon-fill, theme('colors.grey-dark'));
    fill: var(--fill);
  }
}

.VisionIcon--colour-grey-mid {
  .VisionIcon__svg {
    --fill: var(--vision-icon-fill, theme('colors.grey.600'));
    fill: var(--fill);
  }
}

.VisionIcon--colour-red {
  .VisionIcon__svg {
    --fill: var(--vision-icon-fill, theme('colors.red'));
    fill: var(--fill);
  }
}

.VisionIcon--colour-purple {
  .VisionIcon__svg {
    --fill: var(--vision-icon-fill, theme('colors.purple'));
    fill: var(--fill);
  }
}

.VisionIcon--colour-contrast {
  .VisionIcon__svg {
    @apply fill-[var(--contrast-colour)];
  }
}

.VisionIcon--gradient-default {
  --gradient-stop-1-colour: #5c0f99;
  --gradient-stop-2-colour: #3449b8;
  --gradient-stop-3-colour: #1799e1;
  --gradient-stop-2: '60%';

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-default-light {
  --gradient-stop-1-colour: #ae61ea;
  --gradient-stop-2-colour: #526eff;
  --gradient-stop-3-colour: #46bcff;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-miami {
  --gradient-stop-1-colour: #d8417b;
  --gradient-stop-2-colour: #9c7da0;
  --gradient-stop-3-colour: #00b0d0;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-gold {
  --gradient-stop-1-colour: #ba9052;
  --gradient-stop-3-colour: #d6b783;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-dark-chrome {
  --gradient-stop-1-colour: var(--chrome-colour-stop-1);
  --gradient-stop-3-colour: var(--chrome-colour-stop-2);

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-orange-dark {
  --gradient-stop-1-colour: #ff8000;
  --gradient-stop-3-colour: #ffd68b;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-ln4 {
  --gradient-stop-1-colour: #c3e300;
  --gradient-stop-3-colour: theme('colors.ln4-yellow');

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-grape {
  --gradient-stop-1-colour: #44122A;
  --gradient-stop-3-colour: #89303F;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-purple {
  --gradient-stop-1-colour: #6d467d;
  --gradient-stop-3-colour: #9f51dc;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}

.VisionIcon--gradient-custom {
  --gradient-stop-1-colour: #ffc83e;
  --gradient-stop-2-colour: #f536ca;
  --gradient-stop-3-colour: #1799e1;

  .VisionIcon__svg use {
    fill: v-bind(gradientId);
  }
}
</style>
